// Lib
import React from 'react';
import { Building2, Check, LogOut, Plus, Settings } from 'lucide-react';

// Hooks
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { UserLogout } from '@actions/user';
import { AccountLogout } from '@actions/account';
import { SelectOrganization } from '@actions/organization';

// Services
import { applyActiveOrgToken } from '@services/sessionToken';

// UI
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { Separator } from '@components/ui/separator';
import OrganizationInvitationRows from './OrganizationInvitationRows';
import { newOrganizationLabel } from './OrgMenu';

// Types
import type { Organization } from '@app-types/index';
import type { PendingOrganizationInvitation } from '@hooks/useOrganizationInvitations';

interface Profile {
  name?: string;
  avatar?: {
    url?: string;
  };
}

interface MenuProps {
  organizations?: Organization[];
  selectedOrganization?: Pick<Organization, '_id' | 'type'>;
  profile?: Profile;
  onCreateOrganization: () => void;
  setIsShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal?: () => void;
  invitations?: PendingOrganizationInvitation[];
  busyInvitationId?: string | null;
  onAcceptInvitation: (invitationId: string) => Promise<Organization | null>;
  onDeclineInvitation: (invitationId: string) => Promise<boolean>;
}

const menuItemClass =
  'flex w-full items-center gap-2 rounded-lg p-1 cursor-pointer hover:bg-marble-bg-50';

const initials = (name?: string): string =>
  (name ?? '').trim().charAt(0).toUpperCase() || '?';

const Menu: React.FC<MenuProps> = ({
  organizations,
  selectedOrganization,
  profile,
  onCreateOrganization,
  setIsShowMenu,
  closeModal,
  invitations = [],
  busyInvitationId,
  onAcceptInvitation,
  onDeclineInvitation,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSelectOrganization = (organization: Organization): void => {
    dispatch(SelectOrganization(organization));
    if (organization?.token) {
      applyActiveOrgToken(organization.token);
    }

    navigate('/home', { state: { orgSwitchKey: organization?._id } });
    closeModal && closeModal();
    setIsShowMenu(false);
  };

  return (
    <>
      <div
        className={menuItemClass}
        onClick={() => {
          navigate('/settings/account');
          setIsShowMenu(false);
        }}
      >
        <Settings className="size-4 text-marble-bg-700" />
        <p className="text-sm font-semibold text-marble-bg-700">
          Account settings
        </p>
      </div>

      <Separator />

      {!!invitations.length && (
        <div className="flex w-full flex-col gap-1">
          <p className="px-1 text-xs font-semibold tracking-wide text-marble-bg-500 uppercase">
            Invitations
          </p>
          <OrganizationInvitationRows
            invitations={invitations}
            busyInvitationId={busyInvitationId}
            onAccept={onAcceptInvitation}
            onDecline={onDeclineInvitation}
            onCompleted={() => {
              closeModal?.();
              setIsShowMenu(false);
            }}
          />
          <Separator />
        </div>
      )}

      <div className="flex w-full flex-col gap-0.5">
        {organizations && organizations.length ? (
          <div className="flex max-h-60 w-full flex-col gap-0.5 overflow-y-auto [scrollbar-width:thin]">
            {organizations.map((organization) => (
              <div
                key={organization?._id}
                className={menuItemClass}
                onClick={() => onSelectOrganization(organization)}
              >
                <Avatar size="sm">
                  <AvatarImage src={organization?.profile?.avatar?.url} />
                  <AvatarFallback>
                    {initials(organization?.profile?.name)}
                  </AvatarFallback>
                </Avatar>
                <p className="w-[calc(100%-56px)] truncate font-semibold text-marble-bg-700">
                  {organization?.profile?.name}
                </p>
                {selectedOrganization?._id === organization?._id ? (
                  <Check className="size-4 text-marble-bg-700" />
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div
          className={menuItemClass}
          onClick={() => {
            onCreateOrganization();
            setIsShowMenu(false);
          }}
        >
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-marble-bg-50">
            <Plus className="size-4 text-marble-bg-700" />
          </span>
          <p className="w-[calc(100%-32px)] truncate font-semibold text-marble-blue-500">
            {newOrganizationLabel(selectedOrganization)}
          </p>
        </div>

        {/* The desktop rail exposes org settings via the top Org anchor, which
            is hidden on mobile — surface it here so it stays reachable. */}
        <div
          className={menuItemClass}
          onClick={() => {
            navigate('/settings/organization');
            setIsShowMenu(false);
          }}
        >
          <Building2 className="size-4 text-marble-bg-700" />
          <p className="text-sm font-semibold text-marble-bg-700">
            Property settings
          </p>
        </div>
      </div>

      <Separator />

      <div className="flex w-full flex-col gap-0.5">
        <div className={menuItemClass}>
          <Avatar size="sm">
            <AvatarImage src={profile?.avatar?.url} />
            <AvatarFallback>{initials(profile?.name)}</AvatarFallback>
          </Avatar>
          <p className="text-sm font-semibold text-marble-bg-700">
            {profile?.name || ''}
          </p>
        </div>
        <div
          className={menuItemClass}
          onClick={() => {
            dispatch(UserLogout());
            dispatch(AccountLogout());
          }}
        >
          <LogOut className="size-4 text-marble-bg-700" />
          <p className="text-sm font-semibold text-marble-bg-700">Sign out</p>
        </div>
      </div>
    </>
  );
};

export default Menu;
